<template>
  <el-dialog
    :title="isEdit ? 'Edit Room' : 'Add New Room'"
    :visible.sync="dialogVisible"
    width="700px"
    :before-close="handleClose"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="120px">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="Room Number" prop="room_number">
            <el-input v-model="form.room_number" placeholder="e.g., 101, 202"></el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Floor" prop="floor">
            <el-select v-model="form.floor" placeholder="Select floor">
              <el-option label="1st Floor" :value="1"></el-option>
              <el-option label="2nd Floor" :value="2"></el-option>
              <el-option label="3rd Floor" :value="3"></el-option>
              <el-option label="4th Floor" :value="4"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="Room Type" prop="room_type">
            <el-select v-model="form.room_type" placeholder="Select room type" @change="onRoomTypeChange">
              <el-option
                v-for="type in roomTypes"
                :key="type.name"
                :label="type.name"
                :value="type.name"
              ></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Max Occupancy" prop="max_occupancy">
            <el-input-number
              v-model="form.max_occupancy"
              :min="1"
              :max="10"
              controls-position="right"
            ></el-input-number>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="Base Price" prop="base_price">
        <el-input-number
          v-model="form.base_price"
          :min="0"
          :precision="2"
          controls-position="right"
          style="width: 200px"
        >
          <template slot="prepend">$</template>
          <template slot="append">per night</template>
        </el-input-number>
      </el-form-item>

      <el-form-item label="Amenities">
        <el-checkbox-group v-model="form.amenities">
          <el-checkbox label="WiFi"></el-checkbox>
          <el-checkbox label="AC"></el-checkbox>
          <el-checkbox label="TV"></el-checkbox>
          <el-checkbox label="Mini Bar"></el-checkbox>
          <el-checkbox label="Safe"></el-checkbox>
          <el-checkbox label="Balcony"></el-checkbox>
          <el-checkbox label="Kitchen"></el-checkbox>
          <el-checkbox label="Washing Machine"></el-checkbox>
        </el-checkbox-group>
      </el-form-item>

      <el-form-item label="Description">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="3"
          placeholder="Room description"
        ></el-input>
      </el-form-item>

      <el-form-item label="Initial Status" v-if="!isEdit">
        <el-radio-group v-model="form.status">
          <el-radio label="available">Available</el-radio>
          <el-radio label="maintenance">Under Maintenance</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="loading">
        {{ isEdit ? 'Update' : 'Create' }} Room
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import roomService from '@/services/room.service'

export default {
  name: 'CreateRoomDialog',
  props: {
    visible: Boolean,
    room: Object,
    roomTypes: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      dialogVisible: false,
      loading: false,
      form: {
        room_number: '',
        floor: null,
        room_type: '',
        max_occupancy: 2,
        base_price: 0,
        amenities: [],
        description: '',
        status: 'available'
      },
      rules: {
        room_number: [
          { required: true, message: 'Room number is required', trigger: 'blur' }
        ],
        floor: [
          { required: true, message: 'Floor is required', trigger: 'change' }
        ],
        room_type: [
          { required: true, message: 'Room type is required', trigger: 'change' }
        ],
        max_occupancy: [
          { required: true, message: 'Max occupancy is required', trigger: 'blur' }
        ],
        base_price: [
          { required: true, message: 'Base price is required', trigger: 'blur' },
          { type: 'number', min: 0, message: 'Price must be greater than 0', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isEdit () {
      return !!this.room
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
      if (val) {
        this.initializeForm()
      }
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    initializeForm () {
      if (this.isEdit) {
        this.form = { ...this.room }
      } else {
        this.resetForm()
      }
    },

    resetForm () {
      this.form = {
        room_number: '',
        floor: null,
        room_type: '',
        max_occupancy: 2,
        base_price: 0,
        amenities: [],
        description: '',
        status: 'available'
      }
    },

    onRoomTypeChange (typeName) {
      const selectedType = this.roomTypes.find(t => t.name === typeName)
      if (selectedType) {
        this.form.base_price = selectedType.base_price
        this.form.max_occupancy = selectedType.max_occupancy
      }
    },

    handleClose () {
      this.$refs.form.resetFields()
      this.dialogVisible = false
    },

    async handleSubmit () {
      try {
        await this.$refs.form.validate()
        this.loading = true

        if (this.isEdit) {
          await roomService.updateRoom(this.room.id, this.form)
          this.$message.success('Room updated successfully')
        } else {
          await roomService.createRoom(this.form)
          this.$message.success('Room created successfully')
        }

        this.$emit('saved')
        this.handleClose()
      } catch (error) {
        if (error !== false) { // Validation error
          this.$message.error('Failed to save room')
        }
      } finally {
        this.loading = false
      }
    },

    async saveRoom () {
      this.$refs.roomForm.validate(async (valid) => {
        if (valid) {
          try {
            await this.withLoading(async () => {
              await this.$roomService.createRoom(this.room)
              this.showSuccess(this.$t('room.message.created'))
              this.$emit('room-created')
              this.$refs.roomForm.resetFields()
              this.$emit('close', false)
            }, 'loading')
          } catch (error) {
            // Error is already handled by the global interceptor
            console.log('Component caught error in createRoom:', error)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}
</style>
